import { CollabParticipant } from '@livekatsomo/models';
import { Transaction } from 'prosemirror-state';
import { ReplaceStep } from 'prosemirror-transform';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { DecorationSpec } from './types';
import { createTelepointers } from './utils';
import { isReplaceStep } from './isReplaceStep';
import { getValidPos } from './getValidPos';

/**
 * Adjusts the positions of decorations based on changes made by a transaction.
 * Also removes any selection decoration within the change range.
 *
 * @param add - An array of decorations to add.
 * @param remove - An array of decorations to remove.
 * @param tr - The transaction that caused the document change.
 * @param decorationSet - The current decoration set.
 * @param participants - A map of participants in the collaboration session.
 * @returns The updated decoration set.
 */

export function adjustDecorationsOnDocumentChange({
  add,
  remove,
  decorationSet,
  tr,
  participants,
}: {
  add: Decoration[];
  remove: Decoration[];
  tr: Transaction;
  decorationSet: DecorationSet;
  participants: Map<string, CollabParticipant>;
}) {
  // Adjust decoration positions to changes made by the transaction
  try {
    // console.log('adjusting decorations before', decorationSet);
    decorationSet = decorationSet.map(tr.mapping, tr.doc, {
      // Reapplies decorators to those got removed by the state change
      onRemove: (spec: DecorationSpec) => {
        if (spec.clientID) {
          // Get first replacement step that affects the removed decoration
          const step = tr.steps.filter(isReplaceStep)[0] as ReplaceStep;
          if (step) {
            // Get new position for the removed decoration
            const {
              slice: {
                content: { size },
              },
              from,
            } = step;
            const pos = getValidPos(
              tr,
              size
                ? Math.min(from + size, tr.doc.nodeSize - 3)
                : Math.max(from, 1),
            );

            add = [
              ...add,
              ...createTelepointers(
                pos,
                pos,
                spec.clientID,
                false,
                participants.get(spec.clientID)?.name.toUpperCase() || '',
              ),
            ];
          }
        }
      },
    });
    // console.log('adjusting decorations after', decorationSet);
  } catch (err) {
    console.error('Adjust decoration errored', err);
  }

  // Remove any selection decoration within the change range,
  // takes care of the issue when after pasting we end up with a dead selection
  tr.steps.filter(isReplaceStep).forEach((s) => {
    const { from, to } = s as ReplaceStep;
    decorationSet.find(from, to).forEach((deco) => {
      // `from` != `to` means it's a selection
      if (deco.from !== deco.to) {
        remove.push(deco);
      }
    });
  });
  return decorationSet;
}
